import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Icon, Message, Segment, Dimmer, Loader, Table, Modal } from 'semantic-ui-react';
import { api } from '../api/apiClient';
import PaginationFooter from './PaginationFooter';
import { useTablePreferences } from '../hooks/useTablePreferences';

// Renders API response as readable key-value pairs (fallback for generic data)
function FormattedDetailsView({ data, depth = 0 }) {
    if (data === null || data === undefined) {
        return <span style={{ color: '#888' }}>—</span>;
    }
    if (Array.isArray(data)) {
        return (
            <ul style={{ margin: '0.25rem 0', paddingLeft: '1.25rem' }}>
                {data.map((item, idx) => (
                    <li key={idx}>
                        {typeof item === 'object' && item !== null ? (
                            <FormattedDetailsView data={item} depth={depth + 1} />
                        ) : (
                                String(item ?? '')
                        )}
                    </li>
                ))}
            </ul>
        );
    }
    if (typeof data === 'object') {
        const entries = Object.entries(data);
        if (entries.length === 0) return <span style={{ color: '#888' }}>—</span>;
        const paddingLeft = depth > 0 ? '1rem' : '0';
        return (
            <div style={{ paddingLeft, marginTop: depth > 0 ? '0.5rem' : 0 }}>
                {entries.map(([key, value]) => (
                    <div key={key} style={{ marginBottom: '0.5rem' }}>
                        <strong style={{ color: '#062D4D' }}>{key}:</strong>{' '}
                        {typeof value === 'object' && value !== null ? (
                            <FormattedDetailsView data={value} depth={depth + 1} />
                        ) : (
                            <span>{String(value ?? '')}</span>
                        )}
                    </div>
                ))}
            </div>
        );
    }
    return <span>{String(data)}</span>;
}

function formatDisplayValue(value, key = '') {
    if (value === null || value === undefined) return '-';
    if (Array.isArray(value)) return value.length === 0 ? 'None' : value.join(', ');
    const keyLower = (key || '').toLowerCase();
    const isBoolKey = keyLower.endsWith('enabled') || keyLower === 'isunlimited';
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(T|$)/.test(value.trim())) {
        try {
            const d = new Date(value);
            if (!Number.isNaN(d.getTime())) return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
        } catch (_) {}
    }
    if (value === true || value === 'true') return 'Yes';
    if (value === false || value === 'false') return 'No';
    if (isBoolKey && (value === 1 || value === '1')) return 'Yes';
    if (isBoolKey && (value === 0 || value === '0')) return 'No';
    return String(value);
}

function parseStorageValue(raw) {
    if (raw === null || raw === undefined) return null;
    const text = String(raw).trim();
    if (!text) return null;
    const cleaned = text.replace(/,/g, '');
    const match = cleaned.match(/(-?\d+(?:\.\d+)?)\s*([a-zA-Z]+)?/);
    if (!match) return null;
    const num = Number(match[1]);
    if (!Number.isFinite(num)) return null;

    const unitRaw = (match[2] || '').toUpperCase();
    const unit = unitRaw.replace(/B$/, ''); // "GB" -> "G", "GIB" -> "GI"

    const multipliers = {
        '': 1,
        K: 1e3,
        M: 1e6,
        G: 1e9,
        T: 1e12,
        P: 1e15,
        KI: 1024,
        MI: 1024 ** 2,
        GI: 1024 ** 3,
        TI: 1024 ** 4,
        PI: 1024 ** 5,
    };

    const multiplier = multipliers[unit] ?? 1;
    return num * multiplier;
}

function isDateBeforeToday(raw) {
    if (raw === null || raw === undefined || raw === '') return false;
    let d;
    if (raw instanceof Date) {
        d = raw;
    } else if (typeof raw === 'number') {
        d = raw > 1e12 ? new Date(raw) : new Date(raw * 1000);
    } else {
        d = new Date(String(raw));
    }
    if (Number.isNaN(d.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateOnly = new Date(d);
    dateOnly.setHours(0, 0, 0, 0);
    // Treat "today" as at-risk as well (should be highlighted in red).
    return dateOnly <= today;
}

function isDateWithinNextDays(raw, days) {
    if (raw === null || raw === undefined || raw === '' || !Number.isFinite(days) || days <= 0) return false;
    let d;
    if (raw instanceof Date) {
        d = raw;
    } else if (typeof raw === 'number') {
        d = raw > 1e12 ? new Date(raw) : new Date(raw * 1000);
    } else {
        d = new Date(String(raw));
    }
    if (Number.isNaN(d.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(d);
    target.setHours(0, 0, 0, 0);
    // Include "today" in at-risk window.
    if (target < today) return false;
    const diffMs = target.getTime() - today.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    return diffDays <= days;
}

function getTodayDateInputValue() {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

const storageColumns = [
    { label: 'Cloud Name', keys: ['cloudName', 'cloud_name'] },
    { label: 'Validity Date', keys: ['validityDate', 'validity_date'] },
    { label: 'Used Storage', keys: ['usedStorage', 'used_storage'] },
    { label: 'Storage Allowed', keys: ['storageAllowed', 'storage_allowed'] },
    { label: 'Profile', keys: ['profile', 'profileName', 'profile_name'] },
    { label: 'Status', keys: ['status', 'cloudStatus', 'cloud_status'] },
    { label: 'Environment Type', keys: ['st'] },
];

const environmentFilterOptions = [
    { key: 'all', value: 'all', text: 'ALL' },
    { key: 'prod', value: 'prod', text: 'PROD' },
    { key: 'qa', value: 'qa', text: 'QA' },
];

const statusFilterOptions = [
    { key: 'all', value: 'all', text: 'ALL' },
    { key: 'active', value: 'active', text: 'ACTIVE' },
    { key: 'inactive', value: 'inactive', text: 'INACTIVE' },
];

const CloudCapacityDetails = () => {
    const showEnvironmentTypeColumn = false;
    const showActionsColumn = false;
    const [showImportCloudButton, setShowImportCloudButton] = useState(true);
    const [storageData, setStorageData] = useState(null);
    const [storageLoading, setStorageLoading] = useState(false);
    const [storageError, setStorageError] = useState(null);
    const [storageDownloading, setStorageDownloading] = useState(false);
    const [storageDownloadError, setStorageDownloadError] = useState(null);
    const [storageImporting, setStorageImporting] = useState(false);
    const [storageImportError, setStorageImportError] = useState(null);
    const [storageImportSuccess, setStorageImportSuccess] = useState(null);
    const [deletingCloudName, setDeletingCloudName] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [pendingDeleteCloudName, setPendingDeleteCloudName] = useState('');
    const [storageSearchTerm, setStorageSearchTerm] = useState('');
    const [storageSearchResults, setStorageSearchResults] = useState(null);
    const [storageSearchLoading, setStorageSearchLoading] = useState(false);
    const [storageSearchError, setStorageSearchError] = useState(null);
    const [environmentFilter, setEnvironmentFilter] = useState('prod');
    const [statusFilter, setStatusFilter] = useState('active');
    const [sortConfig, setSortConfig] = useState(null);
    const { rowsPerPage, activePage, setActivePage } = useTablePreferences();
    const importFileInputRef = useRef(null);

    const fetchStorageData = () => {
        setStorageError(null);
        setStorageLoading(true);
        return api.licenseGenerator
            .getCloudCapacityAll()
            .then((res) => {
                const raw = res?.data?.data;
                setStorageData(raw);
                setActivePage(1);
            })
            .catch((err) => {
                setStorageData(null);
                setStorageError(
                    err?.response?.data?.message || err?.message || 'Failed to load expiring licenses'
                );
            })
            .finally(() => setStorageLoading(false));
    };

    useEffect(() => {
        fetchStorageData();
    }, []);

    useEffect(() => {
        const fetchImportCloudSetting = async () => {
            try {
                const response = await api.system.getImportCloudEnabled();
                setShowImportCloudButton(!!response?.data?.enabled);
            } catch (error) {
                console.error('Error fetching import cloud setting:', error);
                setShowImportCloudButton(true);
            }
        };
        fetchImportCloudSetting();
    }, []);

    useEffect(() => {
        const handleImportCloudSettingChanged = (event) => {
            const enabled = event?.detail?.enabled;
            if (typeof enabled === 'boolean') {
                setShowImportCloudButton(enabled);
            }
        };

        window.addEventListener('tb:importCloudEnabledChanged', handleImportCloudSettingChanged);
        return () => {
            window.removeEventListener('tb:importCloudEnabledChanged', handleImportCloudSettingChanged);
        };
    }, []);

    useEffect(() => {
        if (!storageImportSuccess && !storageImportError) return undefined;
        const timer = setTimeout(() => {
            setStorageImportSuccess(null);
            setStorageImportError(null);
        }, 5000);
        return () => clearTimeout(timer);
    }, [storageImportSuccess, storageImportError]);

    const handleStorageDownload = () => {
        setStorageDownloadError(null);
        setStorageDownloading(true);
        api.licenseGenerator
            .getCloudCapacityDownload()
            .then((res) => {
                const blob = res?.data;
                if (!blob || !(blob instanceof Blob)) {
                    setStorageDownloadError('Download failed: invalid response');
                    return;
                }
                const disposition = res?.headers?.['content-disposition'] || '';
                const filenameMatch = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i) || disposition.match(/filename=["']?([^"';]+)["']?/i);
                const filename = (filenameMatch && filenameMatch[1]?.trim()) || 'expiring-licenses.xlsx';
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            })
            .catch((err) => {
                const message = err?.response?.data?.message || err?.message || 'Download failed';
                if (err?.response?.data instanceof Blob) {
                    const reader = new FileReader();
                    reader.onload = () => {
                        try {
                            const json = JSON.parse(reader.result);
                            setStorageDownloadError(json?.message || message);
                        } catch (_) {
                            setStorageDownloadError(message);
                        }
                    };
                    reader.readAsText(err.response.data);
                } else {
                    setStorageDownloadError(message);
                }
            })
            .finally(() => setStorageDownloading(false));
    };

    const handleStorageSearch = () => {
        const term = (storageSearchTerm || '').toString().trim();
        if (!term) {
            setStorageSearchResults(null);
            setStorageSearchError(null);
            return;
        }
        setStorageSearchError(null);
        setStorageSearchLoading(true);
        api.licenseGenerator
            .getCloudCapacitySearch(term)
            .then((res) => {
                const raw = res?.data?.data;
                setStorageSearchResults(raw);
                setActivePage(1);
            })
            .catch((err) => {
                setStorageSearchResults(null);
                setStorageSearchError(
                    err?.response?.data?.message || err?.message || 'Search failed'
                );
            })
            .finally(() => setStorageSearchLoading(false));
    };

    const getStorageRowValue = (row, col) => {
        const key = col.keys.find((k) => row[k] !== undefined && row[k] !== null);
        return key != null ? row[key] : row[col.keys[0]];
    };

    const getEnvironmentValue = (row) => {
        const candidates = [
            row.environment,
            row.env,
            row.profile,
            row.profileName,
            row.profile_name,
            row.cloudName,
            row.cloud_name,
        ];
        const normalized = candidates
            .map((v) => (v || '').toString().trim().toLowerCase())
            .find((v) => v.includes('prod') || v.includes('qa'));
        if (!normalized) return '';
        if (normalized.includes('prod')) return 'prod';
        if (normalized.includes('qa')) return 'qa';
        return '';
    };

    const isImportedCloudCapacityRow = (row) =>
        row &&
        typeof row === 'object' &&
        !Array.isArray(row) &&
        Object.prototype.hasOwnProperty.call(row, 'st') &&
        Object.prototype.hasOwnProperty.call(row, 'cloudName') &&
        Object.prototype.hasOwnProperty.call(row, 'validityDate');

    const normalizeStatusValue = (value) => {
        if (value === null || value === undefined || value === '') return '';
        if (value === true) return 'active';
        if (value === false) return 'inactive';
        return String(value).trim().toLowerCase();
    };

    const getStatusValue = (row) => {
        const status = getStorageRowValue(row, storageColumns.find((c) => c.label === 'Status'));
        return normalizeStatusValue(status);
    };

    const getSortableValue = (row, col) => {
        const raw = getStorageRowValue(row, col);
        if (col.label === 'Used Storage' || col.label === 'Storage Allowed') {
            const parsed = parseStorageValue(raw);
            return parsed !== null ? parsed : Number.NEGATIVE_INFINITY;
        }
        if (col.label === 'Validity Date') {
            const d = new Date(raw);
            const time = d.getTime();
            return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
        }
        if (col.label === 'Environment Type') {
            if (!isImportedCloudCapacityRow(row)) return 'mt';
            return (row?.st === true ? 'st' : 'mt');
        }
        return (raw || '').toString().toLowerCase();
    };

    const handleSort = (columnLabel) => {
        setSortConfig((prev) => {
            if (prev && prev.column === columnLabel) {
                if (prev.direction === 'descending') {
                    return null;
                }
                return {
                    column: columnLabel,
                    direction: prev.direction === 'ascending' ? 'descending' : 'ascending',
                };
            }
            return { column: columnLabel, direction: 'ascending' };
        });
        setActivePage(1);
    };
    
    const getSortIconName = (columnLabel) => {
        if (!sortConfig || sortConfig.column !== columnLabel) return 'arrows alternate vertical';
        return sortConfig.direction === 'ascending' ? 'chevron up' : 'chevron down';
    };

    const getSortIconStyle = (columnLabel) => {
        const isActive = !!sortConfig && sortConfig.column === columnLabel;
        return {
            margin: 0,
            verticalAlign: 'middle',
            lineHeight: 1,
            color: isActive ? '#0a66c2' : '#9ca3af',
            opacity: isActive ? 1 : 0.9,
            fontSize: isActive ? '0.95em' : '0.9em',
            transition: 'color 0.15s ease, opacity 0.15s ease'
        };
    };

    const handleImportCloudClick = () => {
        setStorageImportError(null);
        setStorageImportSuccess(null);
        if (importFileInputRef.current) {
            importFileInputRef.current.value = '';
            importFileInputRef.current.click();
        }
    };

    const handleImportFileChange = async (event) => {
        const file = event?.target?.files?.[0];
        if (!file) return;

        const fileName = (file.name || '').toLowerCase();
        const isValidExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
        if (!isValidExcel) {
            setStorageImportError('Please select an Excel file (.xlsx or .xls).');
            return;
        }

        setStorageImportError(null);
        setStorageImportSuccess(null);
        setStorageImporting(true);

        try {
            const res = await api.licenseGenerator.importCloudCapacityFile(file);
            const message = res?.data?.message || 'Cloud capacity file imported successfully.';
            setStorageImportSuccess(message);
            await fetchStorageData();
        } catch (err) {
            setStorageImportError(
                err?.response?.data?.message || err?.message || 'Import failed'
            );
        } finally {
            setStorageImporting(false);
        }
    };

    const handleDeleteImportedCloud = (row) => {
        const cloudName = (row?.cloudName || '').toString().trim();
        if (!cloudName) {
            setStorageImportError('Cloud name is required for delete.');
            setStorageImportSuccess(null);
            return;
        }
        setPendingDeleteCloudName(cloudName);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!pendingDeleteCloudName) return;
        setDeletingCloudName(pendingDeleteCloudName);
        setStorageImportError(null);
        setStorageImportSuccess(null);
        try {
            const res = await api.licenseGenerator.deleteCloudCapacityByName(pendingDeleteCloudName);
            const message = res?.data?.message || `Cloud "${pendingDeleteCloudName}" deleted successfully.`;
            setStorageImportSuccess(message);
            await fetchStorageData();
        } catch (err) {
            setStorageImportError(
                err?.response?.data?.message || err?.message || 'Delete failed'
            );
        } finally {
            setDeletingCloudName('');
            setShowDeleteModal(false);
            setPendingDeleteCloudName('');
        }
    };

    const displayData = storageSearchResults !== null ? storageSearchResults : storageData;
    const isArray = Array.isArray(displayData);
    const rows = isArray ? displayData : (displayData && typeof displayData === 'object' ? [displayData] : []);
    const objRows = rows.filter((r) => typeof r === 'object' && r !== null && !Array.isArray(r));
    const visibleStorageColumns = showEnvironmentTypeColumn
        ? storageColumns
        : storageColumns.filter((col) => col.label !== 'Environment Type');
    const storageAllowedColumn = storageColumns.find((c) => c.label === 'Storage Allowed');
    const sortColumn = sortConfig ? storageColumns.find((c) => c.label === sortConfig.column) : null;
    const filteredRows = objRows
        .filter((row) => {
            if (environmentFilter === 'all') return true;
            return getEnvironmentValue(row) === environmentFilter;
        })
        .filter((row) => {
            const status = getStatusValue(row);
            if (statusFilter === 'all') return true;
            return status === statusFilter;
        });
    const effectiveRows = sortConfig && sortColumn
        ? [...filteredRows].sort((a, b) => {
            const valueA = getSortableValue(a, sortColumn);
            const valueB = getSortableValue(b, sortColumn);
            const comparison = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
            return sortConfig.direction === 'ascending' ? comparison : -comparison;
        })
        : filteredRows;

    const totalPages = Math.max(1, Math.ceil(effectiveRows.length / Math.max(1, rowsPerPage)));
    const pageStart = (activePage - 1) * rowsPerPage;
    const pageEnd = pageStart + rowsPerPage;
    const visibleRows = effectiveRows.slice(pageStart, pageEnd);

    return (
        <div style={{ marginBottom: '1.25rem' }}>
            {(storageImportError || storageImportSuccess) && (
                <Message
                    size="small"
                    negative={!!storageImportError}
                    positive={!!storageImportSuccess}
                    onDismiss={() => {
                        setStorageImportError(null);
                        setStorageImportSuccess(null);
                    }}
                    style={{
                        position: 'fixed',
                        top: 20,
                        right: 20,
                        margin: 0,
                        maxWidth: 420,
                        minWidth: 280,
                        zIndex: 2000,
                        boxShadow: '0 10px 24px rgba(0, 0, 0, 0.16)'
                    }}
                    content={storageImportError || storageImportSuccess}
                />
            )}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '1.35rem',
                    fontWeight: 700,
                    color: '#062D4D',
                    marginBottom: '0.5rem'
                }}
            >
                <Icon name="database" color="blue" />
                <span>Expiring Licenses</span>
            </div>
            <div style={{ fontWeight: 500, color: '#062D4D', marginBottom: '1rem' }}>
                View expiring licenses details in provisioning
            </div>

            <Segment>
                <div
                    style={{
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: '1rem',
                        flexWrap: 'wrap'
                    }}
                >
                    <div
                        style={{
                            flex: 1,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '0.75rem',
                            alignItems: 'flex-end',
                            minWidth: 260
                        }}
                    >
                        <Form.Field style={{ flex: '1 1 260px', marginBottom: 0 }}>
                            <label>Search by cloud name</label>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <Form.Input
                                    placeholder="Enter cloud name (e.g. test)"
                                    value={storageSearchTerm}
                                    onChange={(e, { value }) => setStorageSearchTerm((value || '').toString())}
                                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleStorageSearch())}
                                    icon={storageSearchLoading ? 'spinner' : 'search'}
                                    iconPosition="left"
                                    style={{ flex: 1 }}
                                />
                                <Button
                                    content="Search"
                                    primary
                                    onClick={handleStorageSearch}
                                    loading={storageSearchLoading}
                                    disabled={storageSearchLoading}
                                />
                                {storageSearchResults !== null && (
                                    <Button
                                        content="Clear"
                                        onClick={() => {
                                            setStorageSearchTerm('');
                                            setStorageSearchResults(null);
                                            setStorageSearchError(null);
                                            setActivePage(1);
                                        }}
                                    />
                                )}
                            </div>
                        </Form.Field>
                        <Form.Field style={{ minWidth: 160, marginBottom: 0 }}>
                            <label>Profile</label>
                            <Form.Select
                                options={environmentFilterOptions}
                                value={environmentFilter}
                                onChange={(_, { value }) => {
                                    setEnvironmentFilter((value || 'all').toString().toLowerCase());
                                    setActivePage(1);
                                }}
                            />
                        </Form.Field>
                    </div>
                    <div
                        style={{
                            marginLeft: 'auto',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '0.75rem',
                            alignItems: 'flex-end',
                            justifyContent: 'flex-end'
                        }}
                    >
                        <Form.Field style={{ minWidth: 160, marginBottom: 0 }}>
                            <label>Status</label>
                            <Form.Select
                                options={statusFilterOptions}
                                value={statusFilter}
                                onChange={(_, { value }) => {
                                    setStatusFilter((value || 'all').toString().toLowerCase());
                                    setActivePage(1);
                                }}
                            />
                        </Form.Field>
                        <div>
                            <input
                                ref={importFileInputRef}
                                type="file"
                                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                                style={{ display: 'none' }}
                                onChange={handleImportFileChange}
                            />
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                {showImportCloudButton && (
                                    <Button
                                        icon="upload"
                                        content="Import Cloud"
                                        primary
                                        onClick={handleImportCloudClick}
                                        loading={storageImporting}
                                        disabled={storageImporting}
                                    />
                                )}
                                <Button
                                    primary
                                    icon="download"
                                    content="Download Report"
                                    loading={storageDownloading}
                                    disabled={storageDownloading}
                                    onClick={handleStorageDownload}
                                />
                            </div>
                            {storageDownloadError && (
                                <div style={{ marginTop: '0.25rem', color: '#dc3545', fontSize: '0.85rem' }}>
                                    {storageDownloadError}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div
                    style={{
                        marginTop: '0.75rem',
                        marginBottom: '0.5rem',
                        color: '#b91c1c',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                    }}
                >
                    Note: Values shown in red indicate expired validity, validity expiring within 90 days, or used storage
                    that has reached 90% or more of the allowed storage.
                </div>
                {storageSearchError && (
                    <Message negative style={{ marginBottom: '1rem' }}>
                        <Message.Header>Search error</Message.Header>
                        <p>{storageSearchError}</p>
                    </Message>
                )}
                {storageLoading && (
                    <Dimmer active inverted>
                        <Loader>Loading expiring licenses...</Loader>
                    </Dimmer>
                )}
                {storageError && (
                    <Message negative>
                        <Message.Header>Error</Message.Header>
                        <p>{storageError}</p>
                    </Message>
                )}
                {!storageLoading && !storageError && displayData == null && storageSearchResults === null && (
                    <Message info>No expiring licenses data available.</Message>
                )}
                {!storageLoading && !storageError && displayData != null && storageSearchResults !== null && objRows.length === 0 && (
                    <Message info>No results for &quot;{storageSearchTerm}&quot;.</Message>
                )}
                {!storageLoading && !storageError && displayData != null && objRows.length === 0 && isArray && storageSearchResults === null && (
                    <Message info>No expiring license records.</Message>
                )}
                {!storageLoading && !storageError && displayData != null && objRows.length === 0 && !isArray && (
                    <div style={{ padding: '1rem' }}>
                        <FormattedDetailsView data={displayData} />
                    </div>
                )}
                {!storageLoading && !storageError && displayData != null && objRows.length > 0 && (
                    <>
                        {effectiveRows.length > 0 ? (
                            <>
                                <Table celled striped size="small" style={{ marginTop: 0 }}>
                                    <Table.Header>
                                        <Table.Row>
                                            {visibleStorageColumns.map((col) => (
                                                <Table.HeaderCell
                                                    key={col.label}
                                                    sorted={sortConfig && sortConfig.column === col.label ? sortConfig.direction : undefined}
                                                    onClick={() => handleSort(col.label)}
                                                    style={{ cursor: 'pointer', userSelect: 'none', verticalAlign: 'middle' }}
                                                >
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', lineHeight: 1.2 }}>
                                                        <span>{col.label}</span>
                                                        <Icon
                                                            name={getSortIconName(col.label)}
                                                            style={getSortIconStyle(col.label)}
                                                        />
                                                    </span>
                                                </Table.HeaderCell>
                                            ))}
                                            {showActionsColumn && <Table.HeaderCell style={{ width: 90 }}>Actions</Table.HeaderCell>}
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {visibleRows.map((row, idx) => (
                                            <Table.Row key={pageStart + idx}>
                                                {visibleStorageColumns.map((col) => {
                                                    const rawValue = getStorageRowValue(row, col);
                                                    const isEnvironmentTypeCol = col.label === 'Environment Type';
                                                    const displayValue = formatDisplayValue(rawValue, col.keys[0]);
                                                    const isValidityCol = col.label === 'Validity Date';
                                                    const isStatusCol = col.label === 'Status';
                                                    const isUsedStorageCol = col.label === 'Used Storage';
                                                    const envType = isImportedCloudCapacityRow(row)
                                                        ? row?.st === true
                                                            ? 'ST'
                                                            : 'MT'
                                                        : 'MT';
                                                    const isValidityAtRisk =
                                                        isValidityCol &&
                                                        (isDateBeforeToday(rawValue) || isDateWithinNextDays(rawValue, 90));

                                                    let cellStyle = undefined;
                                                    let content = displayValue;

                                                    if (isValidityCol && isValidityAtRisk) {
                                                        cellStyle = { color: '#dc2626', fontWeight: 600 };
                                                    }

                                                    if (isStatusCol) {
                                                        const normalizedStatus = normalizeStatusValue(rawValue);
                                                        let bg = '#e5e7eb';
                                                        let color = '#111827';
                                                        const statusLabel = normalizedStatus ? normalizedStatus : '-';

                                                        if (normalizedStatus === 'healthy' || normalizedStatus === 'ok' || normalizedStatus === 'active') {
                                                            bg = '#dcfce7';
                                                            color = '#166534';
                                                        } else if (normalizedStatus === 'warning' || normalizedStatus === 'degraded') {
                                                            bg = '#fef9c3';
                                                            color = '#92400e';
                                                        } else if (normalizedStatus === 'critical' || normalizedStatus === 'down' || normalizedStatus === 'failed' || normalizedStatus === 'inactive') {
                                                            bg = '#fee2e2';
                                                            color = '#b91c1c';
                                                        }

                                                        cellStyle = {
                                                            ...cellStyle,
                                                            padding: '0.35rem 0.5rem',
                                                        };

                                                        content = (
                                                            <span
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    minWidth: '72px',
                                                                    padding: '0.15rem 0.4rem',
                                                                    borderRadius: '999px',
                                                                    fontSize: '0.8rem',
                                                                    fontWeight: 600,
                                                                    textTransform: 'capitalize',
                                                                    backgroundColor: bg,
                                                                    color,
                                                                }}
                                                            >
                                                                {statusLabel}
                                                            </span>
                                                        );
                                                    }

                                                    if (isEnvironmentTypeCol) {
                                                        cellStyle = undefined;
                                                        content = envType;
                                                    }

                                                    if (isUsedStorageCol && storageAllowedColumn) {
                                                        const usedNum = parseStorageValue(rawValue);
                                                        const allowedRaw = getStorageRowValue(row, storageAllowedColumn);
                                                        const allowedNum = parseStorageValue(allowedRaw);
                                                        const exceedsThreshold =
                                                            usedNum !== null &&
                                                            allowedNum !== null &&
                                                            allowedNum > 0 &&
                                                            usedNum / allowedNum > 0.9;

                                                        if (exceedsThreshold) {
                                                            cellStyle = {
                                                                ...cellStyle,
                                                                color: '#dc2626',
                                                                fontWeight: 600,
                                                            };
                                                        }
                                                    }

                                                    return (
                                                        <Table.Cell key={col.label} style={cellStyle}>
                                                            {content}
                                                        </Table.Cell>
                                                    );
                                                })}
                                                {showActionsColumn && (
                                                    <Table.Cell>
                                                        {isImportedCloudCapacityRow(row) && row.st === true ? (
                                                            <Button
                                                                size="small"
                                                                negative
                                                                icon="trash"
                                                                loading={deletingCloudName === (row?.cloudName || '').toString().trim()}
                                                                disabled={!!deletingCloudName}
                                                                onClick={() => handleDeleteImportedCloud(row)}
                                                                style={{ flexShrink: 0 }}
                                                            />
                                                        ) : (
                                                            <span>-</span>
                                                        )}
                                                    </Table.Cell>
                                                )}
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table>
                                <PaginationFooter
                                    activePage={activePage}
                                    totalPages={totalPages}
                                    totalItems={effectiveRows.length}
                                    filteredItems={effectiveRows}
                                    hasActiveFilters={environmentFilter !== 'prod' || statusFilter !== 'active'}
                                    onPageChange={setActivePage}
                                    pageStart={pageStart}
                                    pageEnd={pageEnd}
                                />
                            </>
                        ) : (
                            <Message info>
                                {storageSearchResults !== null
                                    ? `No results for "${storageSearchTerm}".`
                                    : 'No expiring license records.'}
                            </Message>
                        )}
                    </>
                )}
            </Segment>

            <Modal
                open={showDeleteModal}
                size="small"
                closeOnDimmerClick={!deletingCloudName}
                closeOnEscape={!deletingCloudName}
                onClose={() => {
                    if (deletingCloudName) return;
                    setShowDeleteModal(false);
                    setPendingDeleteCloudName('');
                }}
            >
                <Modal.Header>Delete Cloud</Modal.Header>
                <Modal.Content>
                    <div>
                        <div style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                            Are you sure you want to delete this cloud?
                        </div>
                        <div style={{ color: '#374151', marginBottom: '0.35rem' }}>
                            <strong>CloudName:</strong> {pendingDeleteCloudName || '-'}
                        </div>
                        <div style={{ color: '#6b7280' }}>
                            This action cannot be undone.
                        </div>
                    </div>
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={() => {
                            setShowDeleteModal(false);
                            setPendingDeleteCloudName('');
                        }}
                        disabled={!!deletingCloudName}
                    >
                        Cancel
                    </Button>
                    <Button
                        negative
                        loading={!!deletingCloudName}
                        disabled={!!deletingCloudName}
                        onClick={handleDeleteConfirm}
                    >
                        Delete
                    </Button>
                </Modal.Actions>
            </Modal>
        </div>
    );
};

export default CloudCapacityDetails;
